/* eslint-disable jsx-a11y/anchor-is-valid */
import { Form, Input, Modal, Popconfirm, Space, Spin, Table, Tooltip, Typography } from "antd";
import React, { useState } from "react";
import { HiOutlineClock, HiOutlineTrash, HiPencilSquare } from "react-icons/hi2";
import { useQueryClient } from "@tanstack/react-query";
import { CreateNotification } from "../../../../../components/notifier";
import { EmailType } from "../../../../../constants";
import { useDemandForSuppliesRequests } from "../../../../procedure/info-technology-process/demand-for-supplies/hooks";
import { ITToolRequestService } from "../../../../../services/it-tools-request/itToolRequest.service";
import { Routes } from "../../../../../AppRoutes";
import { useUser } from "../../../../../hook";
import { useSendMail } from "../../Employees/hooks/use-send-mail";
import { ProcessHistoryModal } from "../../Components/view-history";
import { useColumn } from "../../Employees/hooks/use-column";
import { EditableCell } from "../../Components/editabel-column";
import { useToolGrantColumn } from "./hooks/use-tool-grant-column";
import { usePermissions } from "../../../../role-control/hooks/use-permissions";
import { useCheckPermission } from "../../Permission/hooks/use-check-permission";
import { configPermission } from "../../../../../constants/configPermission";

const ConfigITToolGrantRequest = () => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { demandForSuppliesRequests, isLoading } = useDemandForSuppliesRequests();
  const [requestId, setRequestId] = useState("");
  const { cancel, editingKey, getDataSource, isEditing, isReject, mergedColumns, setEditingKey } = useColumn();
  const { itToolGrantColumn } = useToolGrantColumn();
  const { sendMailDeleteRequest, sendMailUpdateApproval } = useSendMail();
  const { email, name } = useUser();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const isUpdate = useCheckPermission(configPermission.UPDATE);
  const isDelete = useCheckPermission(configPermission.DELETE);

  const dataSource = getDataSource(demandForSuppliesRequests);

  const showDeleteConfirm = (record: any) => {
    const { TextArea } = Input;
    let deleteMessage = "";

    Modal.confirm({
      title: "Thông báo",
      content: (
        <TextArea
          rows={4}
          placeholder="Vui lòng nhập lý do xoá"
          onChange={(e: any) => (deleteMessage = e.target.value)}
        />
      ),
      okText: "Xác nhận",
      okType: "danger",
      okButtonProps: { type: "primary" },
      cancelText: "Huỷ",
      onOk() {
        onRemove(record, deleteMessage);
      },
    });
  };

  const onRemove = async (record: any, deleteMessage: string) => {
    try {
      await ITToolRequestService.deleteITToolRequest(record.id);
      CreateNotification({
        title: "Thành công",
        body: "Xoá phiếu đã chọn thành công",
        icon: "/icons/success.png",
      });
      queryClient.invalidateQueries({ queryKey: ["demand-for-supplies-requests"] });
      const mailParams = {
        mailTo: { name: record.createdName, email: record.createdMail },
        eMailType: EmailType.RDE,
        requestName: `nhu cầu cấp vật tư, CCDC CNTT (số phiếu: ${record.id})`,
        deletor: { name: name, email: email },
        deleteMessage: deleteMessage,
      };
      sendMailDeleteRequest(mailParams);
    } catch (error) {}
  };

  const edit = (record: Partial<any> & { key: React.Key }) => {
    const id = record.id;
    setEditingKey(id);
    form.setFieldsValue({ id, ...record });
  };

  const save = async (record: any) => {
    try {
      const row = (await form.validateFields()) as any;
      const res = await ITToolRequestService.updateITToolRequest(editingKey, row);
      if (res) {
        CreateNotification({
          title: "Thành công",
          body: "Cập nhật thông tin quy trình thành công",
          icon: "/icons/success.png",
        });
        sendMailUpdateApproval(
          record,
          row,
          { approval: EmailType.ITTRA, change: EmailType.ITTRC },
          Routes.demandForSuppliesDetailPage.path.replace(":id", record.id),
        );
      } else
        CreateNotification({
          title: "Không thể cập nhật",
          body: "Không thể cập nhật quy trình, vui lòng thử lại",
          icon: "/icons/warning.png",
        });
      setEditingKey("");
      queryClient.invalidateQueries({ queryKey: ["demand-for-supplies-requests"] });
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const handleOpenHistoryModal = async (record: any) => {
    const res = await ITToolRequestService.getHistory(record.id);
    setHistoryData(res);
    setRequestId(record.id);
    setIsOpenModal(true);
  };

  const onCloseModal = () => {
    setHistoryData([]);
    setIsOpenModal(false);
  };

  const columns = [
    {
      key: "action",
      dataIndex: "action",
      align: "left" as const,
      render: (_: string, record: any) => {
        const editable = isEditing(record);
        return (
          <Space>
            {editable ? (
              <p style={{ width: "80px" }}>
                <Typography.Link onClick={() => save(record)} style={{ marginRight: 8 }}>
                  Lưu
                </Typography.Link>
                <Popconfirm title="Anh/Chị có muốn huỷ?" okText="Đồng ý" cancelText="Không" onConfirm={cancel}>
                  <a>Huỷ</a>
                </Popconfirm>
              </p>
            ) : (
              <>
                {isUpdate && (
                  <Typography.Link disabled={editingKey !== "" || isReject(record)} onClick={() => edit(record)}>
                    <Tooltip title="Chỉnh sửa">
                      <HiPencilSquare
                        size={20}
                        className="cursor-pointer"
                        color={editingKey || isReject(record) ? "#CEE3F3" : "#0ea5e9"}
                      />
                    </Tooltip>
                  </Typography.Link>
                )}
                <Typography.Link disabled={editingKey !== ""} onClick={() => handleOpenHistoryModal(record)}>
                  <Tooltip title="Lịch sử chỉnh sửa">
                    <HiOutlineClock size={20} className="cursor-pointer" color={editingKey ? "#CEE3F3" : "#0ea5e9"} />
                  </Tooltip>
                </Typography.Link>
                {isDelete && (
                  <Typography.Link disabled={editingKey !== ""} onClick={() => showDeleteConfirm(record)}>
                    <Tooltip title="Xoá">
                      <HiOutlineTrash
                        size={20}
                        className="cursor-pointer"
                        color={!editingKey ? "#dc2626" : "#CEE3F3"}
                      />
                    </Tooltip>
                  </Typography.Link>
                )}
              </>
            )}
          </Space>
        );
      },
    },
    ...itToolGrantColumn,
  ];

  return (
    <div>
      <Spin spinning={isLoading}>
        <Form form={form} component={false}>
          <Form.Item>
            <Table
              bordered
              dataSource={dataSource}
              columns={mergedColumns(columns)}
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              rowClassName="editable-row"
              style={{ overflowX: "auto" }}
              scroll={{ x: "2200px" }}
            />
          </Form.Item>
        </Form>
      </Spin>
      <ProcessHistoryModal id={requestId} isOpen={isOpenModal} onClose={onCloseModal} historyData={historyData} />
    </div>
  );
};

export default ConfigITToolGrantRequest;
